import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { getUserToken, getUserRole } from '@/redux/user';
import { UserRoles } from '@/services/innovorder/user/user.type';
import {
    useGetRestaurantDetailsQuery,
    useUpdateRestaurantMutation,
    useGetRestaurantLegalQuery,
    useUpdateRestaurantLegalMutation,
    useDeleteRestaurantMutation,
    useGetPickupSchedulesQuery,
    useSavePickupSchedulesMutation,
} from '@/services/innovorder/restaurant/restaurant.endpoints';
import { useGetBrandRestaurantFamiliesQuery, useGetBrandQuery } from '@/services/innovorder/brand/brand.endpoints';
import type { LegalInfo } from '@/services/innovorder/restaurant/restaurant.type';
import { parseSchedulesToDaySchedules, daySchedulesToSlots } from './schedule.utils';
import type { DaySchedule } from './schedule.utils';

export type RestaurantFormValues = {
    name: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    externalId: string;
    restaurantTag: string;
    brandRestaurantFamilies: number[];
    imageUrl: string;
};

export type LegalFormValues = {
    socialReason: string;
    organizationType: string;
    capital: number | '';
    commerceRegistryCity: string;
    siren: string;
    taxNumber: string;
    industryClassificationCode: string;
    publicationDirectorName: string;
    publicationDirectorFunction: string;
    webmasterName: string;
    webmasterEmail: string;
};

interface ParamTypes {
    brandId: string;
    restaurantId: string;
}

const getDefaultRestaurantValues = (): RestaurantFormValues => ({
    name: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    externalId: '',
    restaurantTag: '',
    brandRestaurantFamilies: [],
    imageUrl: '',
});

const getDefaultLegalValues = (): LegalFormValues => ({
    socialReason: '',
    organizationType: '',
    capital: '',
    commerceRegistryCity: '',
    siren: '',
    taxNumber: '',
    industryClassificationCode: '',
    publicationDirectorName: '',
    publicationDirectorFunction: '',
    webmasterName: '',
    webmasterEmail: '',
});

export const useRestaurantInfoVM = () => {
    const token = useSelector(getUserToken);
    const role = useSelector(getUserRole);
    const { brandId, restaurantId } = useParams<ParamTypes>();

    const isAdmin = role === UserRoles.admin;
    const hasRightOnRestaurantInfo = role === UserRoles.admin || role === UserRoles.brand;

    const { data: restaurant, isLoading: isLoadingRestaurant } = useGetRestaurantDetailsQuery(
        { restaurantId, token },
        { skip: !token },
    );

    const { data: legalInfo, isLoading: isLoadingLegal } = useGetRestaurantLegalQuery(
        { restaurantId, token },
        { skip: !token },
    );

    const { data: brand, isLoading: isLoadingBrand } = useGetBrandQuery(
        { brandId: parseInt(brandId, 10), token },
        { skip: !token },
    );

    const { data: brandFamilies = [] } = useGetBrandRestaurantFamiliesQuery(
        { brandId: parseInt(brandId, 10), token },
        { skip: !token || !hasRightOnRestaurantInfo },
    );

    const { data: pickupSchedulesData, isLoading: isLoadingSchedules } = useGetPickupSchedulesQuery(
        { restaurantId, token },
        { skip: !token },
    );

    const [updateRestaurant, { isLoading: isSavingRestaurant }] = useUpdateRestaurantMutation();
    const [updateLegal, { isLoading: isSavingLegal }] = useUpdateRestaurantLegalMutation();
    const [deleteRestaurant, { isLoading: isDeleting }] = useDeleteRestaurantMutation();
    const [savePickupSchedules, { isLoading: isSavingSchedules }] = useSavePickupSchedulesMutation();

    const [ewalletMinimum, setEwalletMinimum] = useState<number>(0);
    const [initialEwalletMinimum, setInitialEwalletMinimum] = useState<number>(0);

    const [addressData, setAddressData] = useState<{
        name: string;
        lat: number;
        lng: number;
        providerId: string;
    } | null>(null);
    const [initialAddressText, setInitialAddressText] = useState('');
    const [addressText, setAddressText] = useState('');

    const [legalAddressData, setLegalAddressData] = useState<{
        name: string;
        lat: number;
        lng: number;
        providerId: string;
    } | null>(null);
    const [initialLegalAddressText, setInitialLegalAddressText] = useState('');
    const [legalAddressText, setLegalAddressText] = useState('');

    const [pickupSchedules, setPickupSchedules] = useState<DaySchedule[]>([]);
    const initialSchedulesRef = useRef<string>('');
    const [isSchedulesDirty, setIsSchedulesDirty] = useState(false);

    const hasEducationMode = !!brand?.options?.EDUCATION_MODE?.isActivated;

    const restaurantForm = useForm<RestaurantFormValues>({
        defaultValues: getDefaultRestaurantValues(),
        mode: 'onChange',
    });

    const legalForm = useForm<LegalFormValues>({
        defaultValues: getDefaultLegalValues(),
        mode: 'onChange',
    });

    useEffect(() => {
        if (restaurant) {
            const values: RestaurantFormValues = {
                name: restaurant.name || '',
                contactName: restaurant.contactName || '',
                contactEmail: restaurant.contactEmail || '',
                contactPhone: restaurant.contactPhone ? String(restaurant.contactPhone) : '',
                externalId: restaurant.externalId != null ? String(restaurant.externalId) : '',
                restaurantTag: restaurant.restaurantTags?.[0]?.value || '',
                brandRestaurantFamilies:
                    restaurant.brandRestaurantFamilies?.map((f) => f.brandRestaurantFamilyId) || [],
                imageUrl: restaurant.imageUrl || '',
            };
            restaurantForm.reset(values);

            const ewalletVal = restaurant.ewalletMinimumForScanTray ?? 0;
            setEwalletMinimum(ewalletVal);
            setInitialEwalletMinimum(ewalletVal);

            const addr = restaurant.address as
                | { name?: string; lat?: number; lng?: number; providerId?: string }
                | string
                | undefined;
            let text = '';
            if (addr) {
                text = typeof addr === 'string' ? addr : addr.name || '';
            }
            setAddressText(text);
            setInitialAddressText(text);
            setAddressData(null);
        }
    }, [restaurant, restaurantForm]);

    useEffect(() => {
        if (pickupSchedulesData) {
            const parsed = parseSchedulesToDaySchedules(pickupSchedulesData.pickupSchedules ?? []);
            setPickupSchedules(parsed);
            initialSchedulesRef.current = JSON.stringify(parsed);
            setIsSchedulesDirty(false);
        }
    }, [pickupSchedulesData]);

    useEffect(() => {
        if (legalInfo) {
            const values: LegalFormValues = {
                socialReason: legalInfo.socialReason || '',
                organizationType: legalInfo.organizationType || '',
                capital: legalInfo.capital ?? '',
                commerceRegistryCity: legalInfo.commerceRegistryCity || '',
                siren: legalInfo.siren || '',
                taxNumber: legalInfo.taxNumber || '',
                industryClassificationCode: legalInfo.industryClassificationCode || '',
                publicationDirectorName: legalInfo.publicationDirectorName || '',
                publicationDirectorFunction: legalInfo.publicationDirectorFunction || '',
                webmasterName: legalInfo.webmasterName || '',
                webmasterEmail: legalInfo.webmasterEmail || '',
            };
            legalForm.reset(values);
        }
    }, [legalInfo, legalForm]);

    // Legal address display: prefer the Legal's own address; fall back to the restaurant
    // address so the user sees the default ("same as restaurant") when the legal entity
    // has no address row yet. The legal address only gets persisted if the user picks a
    // new one (legalAddressData is non-null).
    useEffect(() => {
        const legalAddrName = legalInfo?.address?.name || '';
        const restaurantAddr = restaurant?.address as { name?: string } | string | undefined;
        const restaurantAddrName = typeof restaurantAddr === 'string' ? restaurantAddr : restaurantAddr?.name || '';
        const text = legalAddrName || restaurantAddrName;
        setLegalAddressText(text);
        setInitialLegalAddressText(text);
        setLegalAddressData(null);
    }, [legalInfo, restaurant]);

    const addressDirtyCount = addressData ? 1 : 0;
    const legalAddressDirtyCount = legalAddressData ? 1 : 0;
    const restaurantDirtyCount = Object.keys(restaurantForm.formState.dirtyFields).length + addressDirtyCount;
    const legalDirtyCount = Object.keys(legalForm.formState.dirtyFields).length + legalAddressDirtyCount;
    const ewalletDirtyCount = ewalletMinimum !== initialEwalletMinimum ? 1 : 0;

    const isLoading = isLoadingRestaurant || isLoadingLegal || isLoadingBrand || isLoadingSchedules;

    const handleAddressChange = useCallback((addr: { name: string; lat: number; lng: number; providerId: string }) => {
        setAddressData(addr);
        setAddressText(addr.name);
    }, []);

    const handleLegalAddressChange = useCallback(
        (addr: { name: string; lat: number; lng: number; providerId: string }) => {
            setLegalAddressData(addr);
            setLegalAddressText(addr.name);
        },
        [],
    );

    const handleSaveRestaurant = useCallback(async () => {
        let caughtError: unknown;
        await restaurantForm.handleSubmit(async (values) => {
            if (!token || !restaurant) return;
            try {
                const { address, created_at: _, ...restaurantData } = restaurant;
                const currentAddress = address as { lat: number; lng: number; providerId?: string } | undefined;
                await updateRestaurant({
                    token,
                    ...restaurantData,
                    brandId: parseInt(brandId, 10),
                    restaurantId,
                    name: values.name,
                    contactEmail: values.contactEmail,
                    contactName: values.contactName,
                    contactPhone: values.contactPhone,
                    externalId: values.externalId,
                    restaurantTags: values.restaurantTag ? [{ value: values.restaurantTag }] : [],
                    brandRestaurantFamilies: values.brandRestaurantFamilies,
                    imageUrl: values.imageUrl,
                    address: addressData
                        ? {
                              lat: addressData.lat,
                              lng: addressData.lng,
                              providerId: addressData.providerId,
                          }
                        : currentAddress
                        ? {
                              lat: currentAddress.lat,
                              lng: currentAddress.lng,
                              providerId: currentAddress.providerId,
                          }
                        : undefined,
                }).unwrap();
                restaurantForm.reset(values);
                if (addressData) {
                    setInitialAddressText(addressData.name);
                    setAddressData(null);
                }
            } catch (e) {
                caughtError = e;
            }
        })();
        if (caughtError !== undefined) throw caughtError;
    }, [token, brandId, restaurantId, restaurant, restaurantForm, updateRestaurant, addressData]);

    const handleCancelRestaurant = useCallback(() => {
        restaurantForm.reset();
        setAddressText(initialAddressText);
        setAddressData(null);
    }, [restaurantForm, initialAddressText]);

    const handleSaveLegal = useCallback(async () => {
        let caughtError: unknown;
        await legalForm.handleSubmit(async (values) => {
            if (!token) return;
            try {
                const body: LegalInfo = {
                    socialReason: values.socialReason || null,
                    organizationType: values.organizationType || null,
                    capital: values.capital === '' ? null : Number(values.capital),
                    commerceRegistryCity: values.commerceRegistryCity || null,
                    siren: values.siren || null,
                    taxNumber: values.taxNumber || null,
                    industryClassificationCode: values.industryClassificationCode || null,
                    publicationDirectorName: values.publicationDirectorName || null,
                    publicationDirectorFunction: values.publicationDirectorFunction || null,
                    webmasterName: values.webmasterName || null,
                    webmasterEmail: values.webmasterEmail || null,
                    ...(legalAddressData ? { googlePlaceId: legalAddressData.providerId } : {}),
                };
                await updateLegal({ token, restaurantId, ...body }).unwrap();
                legalForm.reset(values);
                if (legalAddressData) {
                    setInitialLegalAddressText(legalAddressData.name);
                    setLegalAddressData(null);
                }
            } catch (e) {
                caughtError = e;
            }
        })();
        if (caughtError !== undefined) throw caughtError;
    }, [token, restaurantId, legalForm, updateLegal, legalAddressData]);

    const handleCancelLegal = useCallback(() => {
        legalForm.reset();
        setLegalAddressText(initialLegalAddressText);
        setLegalAddressData(null);
    }, [legalForm, initialLegalAddressText]);

    const handleSaveEwallet = useCallback(async () => {
        if (!token || !restaurant) return;
        const { address, created_at: _, brandRestaurantFamilies, ...restaurantData } = restaurant;
        const currentAddress = address as { lat: number; lng: number; providerId?: string } | undefined;
        await updateRestaurant({
            token,
            ...restaurantData,
            brandId: parseInt(brandId, 10),
            restaurantId,
            ewalletMinimumForScanTray: ewalletMinimum,
            brandRestaurantFamilies: brandRestaurantFamilies?.map((f) => f.brandRestaurantFamilyId),
            address: currentAddress
                ? { lat: currentAddress.lat, lng: currentAddress.lng, providerId: currentAddress.providerId }
                : undefined,
        }).unwrap();
        setInitialEwalletMinimum(ewalletMinimum);
    }, [token, brandId, restaurantId, restaurant, ewalletMinimum, updateRestaurant]);

    const handleCancelEwallet = useCallback(() => {
        setEwalletMinimum(initialEwalletMinimum);
    }, [initialEwalletMinimum]);

    const handleScheduleChange = useCallback((newSchedules: DaySchedule[]) => {
        setPickupSchedules(newSchedules);
        setIsSchedulesDirty(JSON.stringify(newSchedules) !== initialSchedulesRef.current);
    }, []);

    const handleSaveSchedules = useCallback(async () => {
        if (!token) return;
        const slots = daySchedulesToSlots(pickupSchedules);
        await savePickupSchedules({ token, restaurantId, pickupSchedules: slots }).unwrap();
        initialSchedulesRef.current = JSON.stringify(pickupSchedules);
        setIsSchedulesDirty(false);
    }, [token, restaurantId, pickupSchedules, savePickupSchedules]);

    const handleCancelSchedules = useCallback(() => {
        if (pickupSchedulesData) {
            const parsed = parseSchedulesToDaySchedules(pickupSchedulesData.pickupSchedules ?? []);
            setPickupSchedules(parsed);
            setIsSchedulesDirty(false);
        }
    }, [pickupSchedulesData]);

    const handleDelete = useCallback(async () => {
        if (!token) return;
        await deleteRestaurant({ token, restaurantId }).unwrap();
    }, [token, restaurantId, deleteRestaurant]);

    return {
        isLoading,
        isDeleting,
        restaurant,
        brand,
        brandFamilies,
        isAdmin,
        hasRightOnRestaurantInfo,
        hasEducationMode,
        ewalletMinimum,
        restaurantForm,
        legalForm,
        brandId,
        restaurantId,
        restaurantDirtyCount,
        isSavingRestaurant,
        handleSaveRestaurant,
        handleCancelRestaurant,
        legalDirtyCount,
        isSavingLegal,
        handleSaveLegal,
        handleCancelLegal,
        ewalletDirtyCount,
        isSavingEwallet: isSavingRestaurant,
        handleSaveEwallet,
        handleCancelEwallet,
        handleDelete,
        setEwalletMinimum,
        addressText,
        addressData,
        handleAddressChange,
        legalAddressText,
        legalAddressData,
        handleLegalAddressChange,
        pickupSchedules,
        handleScheduleChange,
        isSchedulesDirty,
        isSavingSchedules,
        handleSaveSchedules,
        handleCancelSchedules,
    };
};
